import React, { useEffect, useState } from "react";
import { api } from "../lib/nurseryApi";
import { Plus, Trash, Warning, X } from "@phosphor-icons/react";

const blank = { variety: "", category: "tree", quantity: 0, unit_cost: 0, unit_price: 0, location: "", notes: "" };
const REASONS = ["disease", "pest", "weather", "mishandling", "theft", "other"];

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(blank);
  const [shrinkFor, setShrinkFor] = useState(null);
  const [shrinkQty, setShrinkQty] = useState(1);
  const [shrinkReason, setShrinkReason] = useState("disease");
  const [shrinkLog, setShrinkLog] = useState([]);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/inventory"), api.get("/inventory/shrinkage")]);
    setItems(a.data); setShrinkLog(b.data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    await api.post("/inventory", { ...form, quantity: Number(form.quantity), unit_cost: Number(form.unit_cost), unit_price: Number(form.unit_price) });
    setShowAdd(false); setForm(blank); load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/inventory/${id}`);
    load();
  };
  const submitShrink = async () => {
    await api.post("/inventory/shrinkage", { item_id: shrinkFor.id, quantity: Number(shrinkQty), reason: shrinkReason });
    setShrinkFor(null); setShrinkQty(1); setShrinkReason("disease"); load();
  };

  const totalValue = items.reduce((s, x) => s + Number(x.unit_price) * Number(x.quantity), 0);

  return (
    <div data-testid="inventory-page" className="fade-up">
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Stock Ledger</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 24 }}>
        <div>
          <h1 className="page-title" style={{ marginTop: 8 }}>Inventory</h1>
          <p style={{ color: "var(--muted-fg)", marginTop: 6, fontSize: 14.5 }}>{items.length} varieties · estimated value <b style={{ color: "var(--text)" }}>${Math.round(totalValue).toLocaleString()}</b></p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="add-item-btn"><Plus size={16} weight="bold" /> Add variety</button>
      </div>

      <div className="card" style={{ padding: 0, overflowX: "auto" }}>
        <table className="soft-table">
          <thead>
            <tr>
              <th>Variety</th><th>Category</th><th>Qty</th><th>Cost</th><th>Price</th><th>Location</th><th>Value</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`inv-row-${it.id}`}>
                <td><b>{it.variety}</b><div style={{ fontSize: 11, color: "var(--muted-fg)", marginTop: 2 }}>{it.notes}</div></td>
                <td><span className="badge badge-soft">{it.category}</span></td>
                <td>{it.quantity}</td>
                <td>${Number(it.unit_cost).toFixed(2)}</td>
                <td>${Number(it.unit_price).toFixed(2)}</td>
                <td>{it.location}</td>
                <td><b>${(it.quantity * it.unit_price).toLocaleString()}</b></td>
                <td>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn btn-ghost" style={{ minHeight: 34, padding: "6px 10px" }} onClick={() => setShrinkFor(it)} title="Log shrinkage" data-testid={`shrink-btn-${it.id}`}>
                      <Warning size={16} weight="bold" />
                    </button>
                    <button className="btn btn-ghost" style={{ minHeight: 34, padding: "6px 10px" }} onClick={() => del(it.id)} title="Delete" data-testid={`del-btn-${it.id}`}>
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 32, color: "var(--muted-fg)" }}>No inventory yet. Click "Add variety" to start.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {shrinkLog.length > 0 && (
        <div style={{ marginTop: 36 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Shrinkage log</div>
          <h2 className="font-display" style={{ fontSize: 28, marginTop: 6, marginBottom: 14 }}>Recent losses</h2>
          <div className="card" style={{ padding: 0, overflowX: "auto" }}>
            <table className="soft-table">
              <thead><tr><th>Variety</th><th>Qty lost</th><th>Reason</th><th>Loss $</th><th>When</th></tr></thead>
              <tbody>
                {shrinkLog.map((s) => (
                  <tr key={s.id}>
                    <td><b>{s.variety}</b></td>
                    <td>{s.quantity}</td>
                    <td><span className="badge badge-warn">{s.reason}</span></td>
                    <td>${Number(s.loss_value).toFixed(2)}</td>
                    <td style={{ fontSize: 12, color: "var(--muted-fg)" }}>{new Date(s.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} title="Add variety">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Variety" value={form.variety} onChange={(v) => setForm({ ...form, variety: v })} testid="form-variety" />
            <div>
              <label className="label">Category</label>
              <select className="select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category">
                {["tree", "shrub", "perennial", "annual", "grass"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Quantity" type="number" value={form.quantity} onChange={(v) => setForm({ ...form, quantity: v })} testid="form-qty" />
            <Field label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="form-location" />
            <Field label="Unit cost ($)" type="number" value={form.unit_cost} onChange={(v) => setForm({ ...form, unit_cost: v })} testid="form-cost" />
            <Field label="Unit price ($)" type="number" value={form.unit_price} onChange={(v) => setForm({ ...form, unit_price: v })} testid="form-price" />
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="form-notes" />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
            <button className="btn btn-outline" onClick={() => setShowAdd(false)} data-testid="form-cancel">Cancel</button>
            <button className="btn btn-primary" onClick={add} disabled={!form.variety} data-testid="form-save">Save variety</button>
          </div>
        </Modal>
      )}

      {shrinkFor && (
        <Modal onClose={() => setShrinkFor(null)} title={`Log shrinkage — ${shrinkFor.variety}`}>
          <p style={{ color: "var(--muted-fg)", marginBottom: 14, fontSize: 14 }}>Current qty: <b style={{ color: "var(--text)" }}>{shrinkFor.quantity}</b>. Loss will reduce stock and record dollar loss.</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Quantity lost" type="number" value={shrinkQty} onChange={setShrinkQty} testid="shrink-qty" />
            <div>
              <label className="label">Reason</label>
              <select className="select" value={shrinkReason} onChange={(e) => setShrinkReason(e.target.value)} data-testid="shrink-reason">
                {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
            <button className="btn btn-outline" onClick={() => setShrinkFor(null)}>Cancel</button>
            <button className="btn btn-secondary" onClick={submitShrink} data-testid="shrink-save">Log loss</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(42,51,42,0.45)", backdropFilter: "blur(4px)", display: "grid", placeItems: "center", zIndex: 60, padding: 16 }}>
      <div className="card-soft" style={{ background: "var(--surface)", padding: 24, width: "100%", maxWidth: 640, maxHeight: "90vh", overflow: "auto", borderRadius: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 className="font-display" style={{ fontSize: 26 }}>{title}</h3>
          <button className="btn btn-ghost" onClick={onClose} data-testid="modal-close"><X size={18} weight="bold" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
